/**
 * Back-office shell (D-078, design v2 D-104): translucent macOS-style sidebar with sections and
 * thin lucide icons, sticky toolbar that turns to glass on scroll, ⌘K command palette, account
 * menu. Every page inherits the v2 typography, cards, tables and forms from ui.css + shell.css.
 * v2.1: the accent (and the wallpaper glow) follow the agency's charter colour (Settings → Brand),
 * and the sidebar shows the agency's logo, or its initial.
 */

import { useAgencyBrandCurrentRetrieve, useAuthLogoutCreate, useAuthMeRetrieve } from "@agencyos/api-client";
import {
  Avatar,
  Backdrop,
  type CommandItem,
  CommandPalette,
  Menu,
  OfflineBanner,
  PageHeader,
  ToastProvider,
  applyBrandAccent,
  setThemePreference,
  useCommandShortcut,
  useScrolled,
} from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import {
  BookOpen,
  Building2,
  CalendarDays,
  CalendarRange,
  Clapperboard,
  ConciergeBell,
  EllipsisVertical,
  FileCheck2,
  FileSignature,
  Globe,
  GraduationCap,
  Languages,
  LayoutDashboard,
  ListChecks,
  LifeBuoy,
  LogOut,
  type LucideIcon,
  Megaphone,
  Menu as MenuIcon,
  MessageSquareText,
  Moon,
  Package,
  PhoneCall,
  ReceiptText,
  Rocket,
  Search,
  Settings,
  Share2,
  Sparkles,
  Sun,
  UsersRound,
  Wallet,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { useOnline } from "../lib/online";
import { session, useSession } from "../lib/session";
import { type CurrentMembership, moduleOf, roleIn } from "../lib/settings";
import { LicenceBanner } from "./LicenceBanner";
import { LanguageSwitcher, ThemeSwitcher } from "./Preferences";
import { isRouteDisabled, SaasBanner, useDisabledApps } from "../pages/saas/useSaas";

/** Lucide icons in the chrome: thin strokes, like SF Symbols. */
const ICON = { size: 17, strokeWidth: 1.75 } as const;

export function OfflineNotice() {
  const { t } = useTranslation();
  return useOnline() ? null : <OfflineBanner>{t("offline.banner")}</OfflineBanner>;
}

/** Page frame. With a title, it draws the standard large-title header (eyebrow, title, description,
 * actions); the sections of the page then cascade in (shell.css). */
export function Page({
  children,
  title,
  eyebrow,
  description,
  actions,
  className = "",
}: {
  children: ReactNode;
  title?: ReactNode;
  eyebrow?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <main className={`app-main ${className}`.trim()}>
      {title ? <PageHeader className="page-header" eyebrow={eyebrow} title={title} subtitle={description} actions={actions} /> : null}
      {children}
    </main>
  );
}

/** The agency's charter colour drives the accent of both themes (AA-corrected, see deriveAccent). */
function useAgencyBrand() {
  const brand = useAgencyBrandCurrentRetrieve({ query: { staleTime: 5 * 60_000, retry: false } });
  const identity = brand.data?.status === 200 ? brand.data.data : undefined;
  const color = identity?.primary_color ?? null;
  useEffect(() => {
    applyBrandAccent(color);
  }, [color]);
  useEffect(() => () => applyBrandAccent(null), []);
  return identity;
}

interface NavItem {
  to: string;
  key: string;
  icon: LucideIcon;
  end?: boolean;
  match?: string;
  /** Roles that may open the screen (the API refuses the others); every role when absent. */
  roles?: readonly string[];
  /** D-129: permission codes of the positions matrix (any one opens the screen). */
  permissions?: readonly string[];
  /** D-148: platform screens, shown to the super-admin only. */
  superuser?: boolean;
}

// D-115: the menu follows the rights of the API. The accountant and the trainer never see the
// client conversations, leads or files (P4-06); marketing tools stay with the owner and manager.
// D-120: a position (reception, Academy manager, marketing, support…) acts as one of these roles
// module by module (positions_v1.json): the menu reads the role of the screen's module.
const CLIENT_WORK = ["owner", "manager", "advisor"] as const;
const MONEY = ["owner", "manager", "advisor", "accountant"] as const;
const MARKETING = ["owner", "manager"] as const;
const CONTENT = ["owner", "manager", "advisor"] as const;
const ACADEMY = ["owner", "manager", "advisor", "accountant", "trainer"] as const;

const GROUPS: { key: string; items: NavItem[] }[] = [
  {
    key: "nav.groups.today",
    items: [
      { to: "/", key: "nav.home", icon: LayoutDashboard, end: true },
      { to: "/reception", key: "nav.reception", icon: ConciergeBell, roles: CLIENT_WORK },
      { to: "/agenda", key: "nav.agenda", icon: CalendarDays, roles: CLIENT_WORK },
    ],
  },
  {
    key: "nav.groups.clients",
    items: [
      { to: "/inbox", key: "nav.inbox", icon: MessageSquareText, roles: CLIENT_WORK },
      { to: "/calls", key: "calls.nav", icon: PhoneCall, roles: CLIENT_WORK }, // D-151
      { to: "/leads", key: "nav.crm", icon: UsersRound, roles: CLIENT_WORK },
      { to: "/support", key: "support.nav", icon: LifeBuoy, permissions: ["support.tickets.read", "support.tickets.read_own"] },
    ],
  },
  {
    key: "nav.groups.immigration",
    items: [{ to: "/dossiers", key: "nav.dossiers", icon: FileCheck2, roles: CLIENT_WORK }],
  },
  {
    key: "nav.groups.money",
    items: [
      { to: "/contracts", key: "nav.contracts", icon: FileSignature, roles: CLIENT_WORK },
      { to: "/invoices", key: "nav.invoices", icon: ReceiptText, roles: MONEY },
      { to: "/cash", key: "nav.cash", icon: Wallet, roles: MONEY },
    ],
  },
  {
    key: "nav.groups.offer",
    items: [
      { to: "/catalog", key: "nav.catalog", icon: Package },
      // D-138: a trainer opens Website → Blog to review the articles of their exams
      { to: "/website", key: "nav.website", icon: Globe, roles: [...CONTENT, "trainer"] },
      { to: "/studio", key: "studio.nav", icon: Clapperboard, roles: MARKETING },
      { to: "/ads", key: "ads.nav", icon: Megaphone, roles: MARKETING },
      { to: "/academy-marketing", key: "academyMarketing.nav", icon: Rocket, roles: MARKETING },
      { to: "/social", key: "social.nav", icon: Share2, roles: CONTENT },
      { to: "/academy-admin", key: "nav.academy", icon: GraduationCap, roles: ACADEMY },
      // D-139: official exam sessions (Academy manager, marketing, manager, owner)
      { to: "/exam-dates", key: "examDates.nav", icon: CalendarRange, permissions: ["examdates.manage"] },
      { to: "/bible", key: "nav.bible", icon: BookOpen },
      { to: "/ai-test", key: "nav.aiTest", icon: Sparkles, roles: CLIENT_WORK },
    ],
  },
  {
    key: "nav.groups.admin",
    items: [
      // D-148: the new agency's step-by-step setup (owner and manager)
      { to: "/onboarding", key: "onboarding.nav", icon: ListChecks, roles: ["owner", "manager"] },
      { to: "/settings/agency", key: "nav.settings", icon: Settings, match: "/settings" },
      { to: "/platform/agencies", key: "onboarding.platform.nav", icon: Building2, superuser: true },
    ],
  },
];

/** The menu groups the current position may open (a group with no screen left is hidden). */
function useNavGroups(membership: CurrentMembership | undefined, superuser = false): typeof GROUPS {
  const disabled = useDisabledApps(); // D-147: modules not in the plan
  return useMemo(
    () =>
      GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          if (item.superuser) return superuser;
          if (isRouteDisabled(item.to, disabled)) return false;
          if (item.permissions) {
            const granted = membership?.permissions ?? [];
            return granted.includes("*") || item.permissions.some((code) => granted.includes(code));
          }
          if (!item.roles) return true;
          const role = roleIn(membership, moduleOf(item.to));
          return role !== undefined && item.roles.includes(role);
        }),
      })).filter((group) => group.items.length > 0),
    [membership, superuser, disabled],
  );
}

function useCurrentUser() {
  const me = useAuthMeRetrieve();
  const { organization } = useSession();
  const user = me.data?.status === 200 ? me.data.data : undefined;
  const agency = user?.memberships.find((m) => m.organization_slug === organization) ?? user?.memberships[0];
  return { user, agency };
}

function Sidebar({ onNavigate, onSignOut }: { onNavigate: () => void; onSignOut: () => void }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, agency } = useCurrentUser();
  const groups = useNavGroups(agency as unknown as CurrentMembership | undefined, Boolean(user?.is_superuser));
  const brand = useAgencyBrand();
  const name = user ? user.full_name || user.email : "";
  const initial = (agency?.organization_name || t("app.name")).trim().charAt(0).toUpperCase();
  return (
    <>
      <div className="sidebar__brand">
        <span className={`sidebar__logo${brand?.logo_url ? " sidebar__logo--image" : ""}`} aria-hidden="true">
          {brand?.logo_url ? <img src={brand.logo_url} alt="" /> : initial}
        </span>
        <span className="sidebar__brand-text">
          <strong>{t("app.name")}</strong>
          {agency ? <small>{agency.organization_name}</small> : null}
        </span>
      </div>
      <nav aria-label={t("nav.mainNavigation")} className="sidebar__nav">
        {groups.map((group) => (
          <div key={group.key} className="sidebar__group ao-sidebar__section">
            <p className="sidebar__group-title ao-sidebar__section-title">{t(group.key)}</p>
            {group.items.map(({ to, key, icon: Icon, end, match }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={onNavigate}
                className={({ isActive }) =>
                  `sidebar__link ao-sidebar__item${isActive || (match && pathname.startsWith(match)) ? " active" : ""}`
                }
              >
                <Icon {...ICON} aria-hidden="true" />
                <span>{t(key)}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="sidebar__prefs">
        <LanguageSwitcher />
        <ThemeSwitcher />
      </div>
      {user ? (
        <div className="sidebar__user">
          <Avatar name={name} />
          <span className="sidebar__who">
            <strong>{name}</strong>
            {agency ? <small>{t(`roles.${agency.role}`)}</small> : null}
          </span>
          <Menu
            label={t("design.shell.account")}
            trigger={<EllipsisVertical size={16} strokeWidth={1.75} aria-hidden="true" />}
            align="end"
            placement="up"
            triggerClassName="ao-button ao-button--plain ao-button--sm ao-button--icon"
            items={[
              { heading: agency?.organization_name ?? name },
              {
                label: t("nav.settings"),
                icon: <Settings size={16} strokeWidth={1.75} aria-hidden="true" />,
                onSelect: () => {
                  onNavigate();
                  navigate("/settings/agency");
                },
              },
              { separator: true },
              {
                label: t("auth.logout"),
                icon: <LogOut size={16} strokeWidth={1.75} aria-hidden="true" />,
                danger: true,
                onSelect: onSignOut,
              },
            ]}
          />
        </div>
      ) : null}
    </>
  );
}

function useCommands(onSignOut: () => void): CommandItem[] {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, agency } = useCurrentUser();
  const groups = useNavGroups(agency as unknown as CurrentMembership | undefined, Boolean(user?.is_superuser));
  return useMemo(() => {
    const pages = groups.flatMap((group) =>
      group.items.map<CommandItem>(({ to, key, icon: Icon }) => ({
        id: `page:${to}`,
        label: t(key),
        group: t("design.shell.pages"),
        hint: t(group.key),
        icon: <Icon size={16} strokeWidth={1.75} aria-hidden="true" />,
        onSelect: () => navigate(to),
      })),
    );
    const actions: CommandItem[] = [
      {
        id: "theme:light",
        label: t("design.shell.themeTo", { name: t("settings.themeLight") }),
        group: t("design.shell.actions"),
        icon: <Sun size={16} strokeWidth={1.75} aria-hidden="true" />,
        keywords: "theme light clair",
        onSelect: () => setThemePreference("light"),
      },
      {
        id: "theme:dark",
        label: t("design.shell.themeTo", { name: t("settings.themeDark") }),
        group: t("design.shell.actions"),
        icon: <Moon size={16} strokeWidth={1.75} aria-hidden="true" />,
        keywords: "theme dark sombre",
        onSelect: () => setThemePreference("dark"),
      },
      {
        id: "language",
        label: t("design.shell.switchLanguage"),
        group: t("design.shell.actions"),
        icon: <Languages size={16} strokeWidth={1.75} aria-hidden="true" />,
        keywords: "langue language",
        onSelect: () => void i18n.changeLanguage(i18n.resolvedLanguage === "en" ? "fr" : "en"),
      },
      {
        id: "logout",
        label: t("auth.logout"),
        group: t("design.shell.actions"),
        icon: <LogOut size={16} strokeWidth={1.75} aria-hidden="true" />,
        onSelect: onSignOut,
      },
    ];
    return [...pages, ...actions];
  }, [t, i18n, navigate, onSignOut, groups]);
}

export function AppShell() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const logout = useAuthLogoutCreate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [palette, setPalette] = useState(false);
  const scrolled = useScrolled();
  useEffect(() => setOpen(false), [pathname]);
  useCommandShortcut(() => setPalette(true));

  const signOut = useMemo(
    () => () => {
      logout.mutate(
        { data: {} },
        {
          onSettled: () => {
            queryClient.clear();
            session.signOut();
          },
        },
      );
    },
    [logout, queryClient],
  );
  const commands = useCommands(signOut);
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

  return (
    <ToastProvider dismissLabel={t("design.shell.dismiss")}>
      <div className={`shell${open ? " shell--menu-open" : ""}`}>
        <Backdrop />
        <aside className="shell__sidebar ao-glass ao-glass--thin ao-sidebar" aria-label={t("nav.mainNavigation")}>
          <Sidebar onNavigate={() => setOpen(false)} onSignOut={signOut} />
        </aside>
        {open ? (
          <button type="button" className="shell__scrim" aria-label={t("nav.closeMenu")} onClick={() => setOpen(false)} />
        ) : null}
        <div className="shell__main">
          <header className="shell__topbar ao-toolbar" data-scrolled={scrolled}>
            <button
              type="button"
              className="shell__menu-button ao-button ao-button--plain ao-button--icon"
              aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
              aria-expanded={open}
              onClick={() => setOpen((value) => !value)}
            >
              {open ? <X size={20} strokeWidth={1.75} aria-hidden="true" /> : <MenuIcon size={20} strokeWidth={1.75} aria-hidden="true" />}
            </button>
            <button
              type="button"
              className="ao-search-trigger shell__search"
              aria-label={t("design.shell.search")}
              aria-keyshortcuts={mac ? "Meta+K" : "Control+K"}
              onClick={() => setPalette(true)}
            >
              <Search size={15} strokeWidth={2} aria-hidden="true" />
              <span>{t("design.shell.searchPlaceholder")}</span>
              <kbd className="ao-kbd" aria-hidden="true">
                {mac ? "⌘K" : "Ctrl K"}
              </kbd>
            </button>
            <div className="shell__tools">
              <LanguageSwitcher />
              <ThemeSwitcher />
            </div>
          </header>
          <div className="app-notices">
            <OfflineNotice />
            <SaasBanner />
            <LicenceBanner />
          </div>
          <Outlet />
        </div>
        <CommandPalette
          open={palette}
          onClose={() => setPalette(false)}
          items={commands}
          label={t("design.shell.palette")}
          placeholder={t("design.shell.palettePlaceholder")}
          emptyText={t("design.shell.noResult")}
          hints={{ navigate: t("design.shell.hintNavigate"), open: t("design.shell.hintOpen"), close: t("design.shell.hintClose") }}
          searchIcon={<Search size={18} strokeWidth={1.75} aria-hidden="true" />}
        />
      </div>
    </ToastProvider>
  );
}
